import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {formatMinor} from '@/entities/salary/money';
import type {PayrollTotal} from '@/entities/salary/types';
import {Card} from '@/shared/ui/card';
import {Explain, type Definition} from '@/shared/ui/explain';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Итог ведомости — и ровно столько правды, сколько в нём есть.
 *
 * Две вещи, которые здесь нельзя делать, и обе выглядят как «улучшение»:
 *
 * **Нельзя свернуть `totals` в одно число.** Это массив, по строке на валюту.
 * Складывать сумы с долларами по выдуманному курсу — худшее, что может сделать
 * ведомость: получится единственное число на экране, которое выглядит как ответ и не
 * является им ни при каком курсе.
 *
 * **Нельзя спрятать `skipped_rows`.** Строки с флагами в итог не входят. Итог без
 * счётчика пропущенных выглядит полным — и бухгалтер, у которого пятеро из тридцати
 * без оклада, узнает об этом от кассы, а не от экрана. Поэтому счётчик стоит РЯДОМ с
 * суммой, а не в сноске под таблицей.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(2)},
  headText: {flex: 1, gap: 2},
  lines: {gap: spacing(2)},
  line: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing(3),
  },
  amount: {flexShrink: 1, textAlign: 'right'},
});

export function PayrollTotals({
  totals,
  skipped,
  counted,
}: {
  totals: readonly PayrollTotal[];
  /** `skipped_rows` — сколько строк не вошло в итог. */
  skipped: number;
  /** Сколько строк вошло: `rows.length - skipped`, посчитано вызывающим. */
  counted: number;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  /**
   * Формула у первых двух есть, у «пропущено» её нет: это счётчик, а не вычисление, и
   * `Explain` рисует блок формулы только там, где она действительно есть.
   */
  const definitions: Definition[] = [
    {
      term: t('payroll.def.monthly.term'),
      meaning: t('payroll.def.monthly.meaning'),
      formula: t('payroll.def.monthly.formula'),
    },
    {
      term: t('payroll.def.hourly.term'),
      meaning: t('payroll.def.hourly.meaning'),
      formula: t('payroll.def.hourly.formula'),
    },
    {term: t('payroll.def.totals.term'), meaning: t('payroll.def.totals.meaning')},
    {term: t('payroll.def.skipped.term'), meaning: t('payroll.def.skipped.meaning')},
  ];

  return (
    <Card padding={4} style={styles.root} testID="payroll-totals">
      <View style={styles.head}>
        <View style={styles.headText}>
          <AppText variant="heading">{t('payroll.totals')}</AppText>
          <AppText variant="caption">
            {t('payroll.totalsHint', {count: counted})}
          </AppText>
        </View>
        <Explain
          testID="explain-payroll"
          title={t('payroll.totals')}
          subtitle={t('payroll.totalsHint', {count: counted})}
          definitions={definitions}
          note={t('payroll.notPayslip')}
        />
      </View>

      {totals.length === 0 ? (
        <AppText variant="monoLg" color={p.mutedForeground} testID="payroll-total-empty">
          {t('payroll.totalsEmpty')}
        </AppText>
      ) : (
        <View style={styles.lines}>
          {totals.map(total => (
            <View key={total.currency} style={styles.line}>
              <AppText variant="eyebrow">{total.currency}</AppText>
              <AppText
                variant="metricSm"
                style={styles.amount}
                testID={`payroll-total-${total.currency}`}>
                {formatMinor(total.accrued_minor)}
              </AppText>
            </View>
          ))}
        </View>
      )}

      {/* Рядом с итогом, а не под таблицей: см. модульный комментарий. */}
      {skipped > 0 ? (
        <AppText variant="body" color={p.warningInk} testID="payroll-skipped">
          {t('payroll.skipped', {count: skipped})}
        </AppText>
      ) : null}
    </Card>
  );
}
